/**
 * t258u: the author edits or deletes their own task comment, the same way on the phone and the desktop.
 * Editing shows the text with @Names (the <@id> tokens decoded) and saves only when it actually changed; deleting asks
 * first. Every list of the task's comments (task panel, phone task, feed comments sheet, feedback) refetches after.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RealtimeEvents, topics, type TaskComment } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { ask } from '@/components/ui';
import { decodeMentions, encodeMentions } from '@/lib/mentions';
import { realtime } from '@/lib/realtime';
import { useUi } from '@/lib/store';

/** The editable text of a comment and the @Name -> id map that turns it back into tokens. */
export function commentDraft(c: Pick<TaskComment, 'body'>, nameOf: (id: string) => string) {
  return decodeMentions(c.body, nameOf);
}

/** The body to save, or null when nothing changed (or it was emptied: delete is its own action). */
export function editedBody(c: Pick<TaskComment, 'body'>, draft: string, picked: Map<string, string>): string | null {
  const body = encodeMentions(draft.trim(), picked).body;
  if (!body || body === c.body || body === c.body.trim()) return null;
  return body;
}

export function useTaskCommentActions(nameOf: (id: string) => string) {
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const [editing, setEditing] = useState<{ id: string; text: string; picked: Map<string, string> } | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = (taskId: string) => Promise.all([
    qc.invalidateQueries({ queryKey: ['task-comments', taskId] }),
    qc.invalidateQueries({ queryKey: ['task', taskId] }),
    qc.invalidateQueries({ queryKey: ['task-activity', taskId] }),
  ]);
  return {
    editing,
    setText: (text: string) => setEditing(e => (e ? { ...e, text } : e)),
    start: (c: TaskComment) => { const d = commentDraft(c, nameOf); setEditing({ id: c.id, text: d.text, picked: d.picked }); },
    cancel: () => setEditing(null),
    busy,
    save: async (c: TaskComment) => {
      if (!editing || editing.id !== c.id) return;
      const body = editedBody(c, editing.text, editing.picked);
      if (body === null) { setEditing(null); return; }
      setBusy(true);
      try { await api.editTaskComment(c.id, body); setEditing(null); await refresh(c.task_id); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
    },
    remove: async (c: TaskComment) => {
      if (!await ask({ title: 'Delete this comment?', confirmLabel: 'Delete' })) return;
      try { await api.deleteTaskComment(c.id); await refresh(c.task_id); toast('Comment deleted', 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
    },
  };
}

/**
 * Live: someone added, edited or deleted a comment on this task (the project's realtime topic). t574u: a task with no
 * project has no topic anyone may join (the server refuses it), so it isn't subscribed.
 */
export function useLiveTaskComments(taskId: string | null | undefined, projectId: string | null | undefined, noProject?: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!taskId || !projectId || noProject) return;
    return realtime.subscribe(topics.project(projectId), (event, payload) => {
      if (event === RealtimeEvents.TaskCommented && String(payload.task_id) === taskId) void qc.invalidateQueries({ queryKey: ['task-comments', taskId] });
    });
  }, [taskId, projectId, noProject, qc]);
}
